'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createClient } from '@/utils/supabase/client';
import { checkPotentialAccess as evaluatePotentialAccess, getPotentialProgress, resolveCommercialStage } from '@/lib/potential-access';
import { hasActiveSubscription, hasActiveTrial } from '@/lib/services/commercial-access.mjs';

const supabase = createClient();

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commercialState, setCommercialState] = useState({
    stage: 'visitor',
    plan: 'free',
    accountAgeDays: 0,
    trialEndsAt: null,
    hasActiveSubscription: false,
    potentialProgress: 30
  });

  const loadCommercialState = useCallback(async (authUser) => {
    if (!authUser?.id) {
      setCommercialState({
        stage: 'visitor',
        plan: 'free',
        accountAgeDays: 0,
        trialEndsAt: null,
        hasActiveSubscription: false,
        potentialProgress: 30
      });
      return;
    }

    try {
      const [
        { data: profile },
        { data: userRow },
        { data: trialRow },
        { data: subscriptionRow },
      ] = await Promise.all([
        supabase
          .from('profiles')
          .select('created_at')
          .eq('user_id', authUser.id)
          .maybeSingle(),
        supabase
          .from('usuarios')
          .select('plan, trial_ends_at, trial_expires_at')
          .eq('auth_uuid', authUser.id)
          .maybeSingle(),
        supabase
          .from('trials')
          .select('status, trial_end')
          .eq('user_id', authUser.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('subscriptions')
          .select('status, plan_type, current_period_end')
          .eq('user_id', authUser.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
      ]);

      const createdAt = profile?.created_at || authUser.created_at || null;
      const ageMs = createdAt ? Date.now() - new Date(createdAt).getTime() : 0;
      const accountAgeDays = createdAt ? Math.max(0, Math.floor(ageMs / (1000 * 60 * 60 * 24))) : 0;

      const trialEndsAt = userRow?.trial_ends_at || userRow?.trial_expires_at || null;
      const plan = userRow?.plan || 'free';
      const trialActive = hasActiveTrial(trialRow) || hasActiveTrial(userRow);
      const subscriptionActive = hasActiveSubscription(subscriptionRow);
      const stage = resolveCommercialStage({
        isAuthenticated: true,
        hasActiveSubscription: subscriptionActive,
        hasActiveTrial: trialActive,
      });

      setCommercialState({
        stage,
        plan,
        accountAgeDays,
        trialEndsAt,
        hasActiveSubscription: subscriptionActive,
        potentialProgress: getPotentialProgress(stage)
      });
    } catch {
      setCommercialState((prev) => ({
        ...prev,
        stage: 'free',
        potentialProgress: getPotentialProgress('free')
      }));
    }
  }, []);

  useEffect(() => {
    // Obtener sesión actual
    supabase.auth.getSession().then(({ data: { session } }) => {
      const authUser = session?.user ?? null;
      setUser(authUser);
      loadCommercialState(authUser);
      setLoading(false);
    });

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const authUser = session?.user ?? null;
      setUser(authUser);
      loadCommercialState(authUser);
    });

    return () => subscription.unsubscribe();
  }, [loadCommercialState]);

  const checkPotentialAccess = useCallback(
    (featureName) => evaluatePotentialAccess(featureName, commercialState),
    [commercialState]
  );

  const value = useMemo(() => ({
    user,
    loading,
    commercialState,
    checkPotentialAccess,
    signOut: () => supabase.auth.signOut(),
  }), [user, loading, commercialState, checkPotentialAccess]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}



